<!-- NoteSection.vue -->
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

import SearchNoteInput from "./SearchNoteInput.vue";
import NoteFilter from "./NoteFilter.vue";
import BulkActionNoteButton from "./NoteActionsButton.vue";
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue"; // ✅ NOVO
import NoteList from "./NoteList.vue";
import BulkAddNoteForm from "./BulkAddNoteForm.vue";

// ✅ NOVO: modal de deleção em lote
import BatchDeleteNoteForm from "./BatchDeleteNoteForm.vue";

// ✅ NOVO: modal de edição em lote
import BulkEditNoteForm from "./BulkEditNoteForm.vue";

// ✅ NOVO: nota avulsa padrão (offsets 0/0)
import AddDetachedNoteForm from "./AddDetachedNoteForm.vue";

// >>> NOVO: nota avulsa com IA (offsets 0/0)
import AddAiDetachedNoteForm from "./AddAiDetachedNoteForm.vue";

import DetachedNoteButton from "./DetachedNoteButton.vue";

import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
// Mesmo gate por nota do checkbox individual (NoteList) — usado para filtrar o
// "marcar todas" e não montar um lote que o backend rejeitaria por inteiro.
import { useNoteManagePermission } from "@/composables/useNoteManagePermission";

const noteStore = useNoteStore();
const activityStore = useActivityStore();
const textStore = useTextStore();
const userStore = useUserStore();

const { canManageNote } = useNoteManagePermission(() => ({
  userId: userStore.currentUser?.userId,
  textCreatedByUserId: textStore.selectedText?.createdByUserId,
  activityCreatedByUserId: activityStore.selectedActivity?.createdByUserId,
}));

// ✅ PATCH: toast local para notificações vindas de NoteList/DeleteNoteForm
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("success");
const toastTimeoutId = ref(null);

// >>> NOVO: o watcher de filtros só reage a mudanças feitas pelo usuário DEPOIS que
// o carregamento inicial (loadInitialNotes, incluindo o fallback de "Minhas notas
// vazio") terminar. Depende de NoteSection remontar a cada troca de atividade — a
// rota "right-area" não tem :activityId (router/index.js), e toda navegação para
// ela vem de outra rota (ActivityForm/DashboardView/NoteGeneralForm), então o
// componente sempre desmonta/remonta ao trocar de atividade.
const hasCompletedInitialLoad = ref(false);

function resetToastTimeout(duration = 3000) {
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }

  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function handleNotify(payload) {
  toastType.value =
    payload?.type === "error"
      ? "error"
      : payload?.type === "info"
      ? "info"
      : "success";
  toastMessage.value =
    payload?.message ||
    (toastType.value === "error"
      ? "Ocorreu um erro ao excluir a nota."
      : "Nota apagada com sucesso.");

  showToast.value = true;
  resetToastTimeout(toastType.value === "error" ? 6000 : 3000);
}

// ✅ NOVO: controle do modal de deleção em lote
const showBatchDeleteModal = ref(false);

// ✅ NOVO: controle do modal de edição em lote
const showBulkEditModal = ref(false);

// ✅ NOVO: controle do modal de nota avulsa padrão
const showDetachedNoteModal = ref(false);

// >>> NOVO: controle do modal de nota avulsa com IA
const showAiDetachedNoteModal = ref(false);

// >>> NOVO: espelha o estado isAiNoteMode do TextDisplayer.vue.
// Atualizado via evento global "ai-note-mode-changed" disparado pelo TextDisplayer.
// Não usa store para não contaminar o estado global com lógica puramente de UI.
const isAiNoteMode = ref(false);

// ✅ NOVO: abre modal de deleção em lote (só se houver selecionadas)
function openBatchDeleteModal() {
  if (
    !Array.isArray(noteStore.selectedNoteIds) ||
    noteStore.selectedNoteIds.length === 0
  )
    return;
  showBatchDeleteModal.value = true;
}

// ✅ NOVO: abre modal de edição em lote (só se houver selecionadas)
function openBulkEditModal() {
  if (
    !Array.isArray(noteStore.selectedNoteIds) ||
    noteStore.selectedNoteIds.length === 0
  )
    return;
  showBulkEditModal.value = true;
}

// ✅ NOVO: abre o modal de nota avulsa correto conforme o modo ativo.
// Se isAiNoteMode === true → abre AddAiDetachedNoteForm (IA, offsets 0/0).
// Se isAiNoteMode === false → abre AddDetachedNoteForm (padrão, offsets 0/0).
// Isso preserva integralmente o comportamento original do fluxo padrão.
function openDetachedModal() {
  const activityId = activityStore.idActivity;
  if (!activityId) return;

  if (isAiNoteMode.value) {
    showAiDetachedNoteModal.value = true;
  } else {
    showDetachedNoteModal.value = true;
  }
}

// ✅ NOVO: fecha modal de deleção
function closeBatchDeleteModal() {
  showBatchDeleteModal.value = false;
}

// ✅ NOVO: fecha modal de edição em lote
function closeBulkEditModal() {
  showBulkEditModal.value = false;
}

// ✅ NOVO: fecha modal de nota avulsa padrão
function closeDetachedModal() {
  showDetachedNoteModal.value = false;
}

// >>> NOVO: fecha modal de nota avulsa com IA
function closeAiDetachedModal() {
  showAiDetachedNoteModal.value = false;
}

// >>> NOVO: handler do evento global emitido por TextDisplayer ao alternar isAiNoteMode
function handleAiNoteModeChanged(event) {
  isAiNoteMode.value = !!event.detail?.isAiNoteMode;
}

// Toast de sucesso emitido por EditNoteForm quando aberto de dentro da lista
// (NoteCrudDropdown): a linha da lista é desmontada no refetch do save, então o
// componente não consegue exibir o próprio toast — este ancestral estável exibe.
function handleNoteActionToast(event) {
  handleNotify(event.detail || {});
}

// Notas EM EXIBIÇÃO que o usuário logado pode gerenciar (editar/excluir) — as
// mesmas para as quais o checkbox individual aparece em NoteList. O "marcar
// todas" opera SOMENTE sobre elas: incluir notas alheias derrubaria o lote
// inteiro no backend (bulk-update/delete são atômicos).
//
// Lê displayedNotes, não `notes`: sob o foco por trecho a lista mostra o recorte
// do trecho clicado, e "marcar todas" precisa casar com o que está na tela.
const manageableNotesOnPage = computed(() => {
  const notesOnPage = Array.isArray(noteStore.displayedNotes)
    ? noteStore.displayedNotes
    : [];
  return notesOnPage.filter((n) => canManageNote(n));
});

// Só faz sentido oferecer o checkbox-all se houver ao menos uma nota gerenciável.
const anySelectableOnPage = computed(
  () => manageableNotesOnPage.value.length > 0
);

// ✅ igual à lógica de AuthorForm, mas para notas — restrito às gerenciáveis
const isAllSelectedOnPage = computed({
  get: () => {
    const manageable = manageableNotesOnPage.value;
    if (manageable.length === 0) return false;

    return manageable.every((n) =>
      noteStore.selectedNoteIds.includes(n.noteId)
    );
  },
  set: (value) => {
    const idsOnPage = manageableNotesOnPage.value.map((n) => n.noteId);

    if (value) {
      idsOnPage.forEach((id) => {
        if (!noteStore.selectedNoteIds.includes(id)) {
          noteStore.selectedNoteIds.push(id);
        }
      });
    } else {
      noteStore.selectedNoteIds = noteStore.selectedNoteIds.filter(
        (id) => !idsOnPage.includes(id)
      );
    }
  },
});

/* =========================================================
     ✅ CONTROLE DO MODAL DE ADIÇÃO EM LOTE
  ========================================================= */
const showBulkAddModal = ref(false);

const bulkExcerpts = computed(() => noteStore.multiSelectedExcerpts || []);

const hasSelectedExcerpts = computed(
  () => Array.isArray(bulkExcerpts.value) && bulkExcerpts.value.length > 0
);

function openBulkAddModal() {
  if (!hasSelectedExcerpts.value) return;

  showBulkAddModal.value = true;
  window.dispatchEvent(new CustomEvent("bulk-add-notes-opened"));
}

async function handleBulkNotesCreated() {
  const activityId = activityStore.idActivity;
  if (!activityId) return;

  await noteStore.getNotesByActivityId(activityId, true, true, false, []);
  window.dispatchEvent(new CustomEvent("note-list-reloaded"));

  handleBulkAddClose();
}

function handleBulkAddClose() {
  showBulkAddModal.value = false;

  if ("multiSelectedExcerpts" in noteStore) {
    noteStore.multiSelectedExcerpts = [];
  }

  window.dispatchEvent(new CustomEvent("clear-multi-selected-excerpts"));
}

/* =========================================================
     ✅ WATCHER DE FILTROS
  ========================================================= */
watch(
  [
    () => noteStore.filterIncludeOwn,
    () => noteStore.filterIncludeComments,
    () => noteStore.filterSortOrder,
    () => noteStore.filterIncludeSelectedUsers,
    () => noteStore.selectedUserIdsForFilter,
  ],
  async () => {
    // Ignora mudanças disparadas pelo próprio loadInitialNotes() (reset + decisão
    // do fallback) — essas já são tratadas ali, num único ciclo de fetch.
    if (!hasCompletedInitialLoad.value) return;

    await noteStore.setNoteFilters({
      includeOwn: noteStore.filterIncludeOwn,
      comments: noteStore.filterIncludeComments,
      sortBy: noteStore.filterSortOrder,
      includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
    });
  }
);

/* =========================================================
     ✅ CARREGAMENTO INICIAL DAS NOTAS
  ========================================================= */
// >>> NOVO: carrega as notas da atividade decidindo o filtro correto (Minhas notas
// OU o usuário que mais postou, se "Minhas notas" viria vazio) ANTES de buscar a
// lista paginada — um único ciclo de loading, sem estados intermediários incorretos.
async function loadInitialNotes(activityId) {
  // Liga o spinner já de cara — evita qualquer estado vazio piscando enquanto
  // decide o filtro inicial.
  noteStore.isLoadingNotes = true;

  // Limpa dado/filtro que possam ter sobrado de uma atividade anterior (a store
  // do Pinia não é resetada ao trocar de atividade).
  noteStore.notes = [];
  noteStore.allNotes = [];
  noteStore.filterIncludeOwn = true;
  noteStore.filterIncludeComments = false;
  noteStore.filterIncludeSelectedUsers = false;
  noteStore.selectedUserIdsForFilter = [];

  await noteStore.getNotesByActivityId(activityId, true, true, false, []);

  // Decide o filtro ANTES de buscar a lista paginada — evita buscar "Minhas
  // notas" (que sabemos que pode vir vazia) e só depois trocar.
  const currentUserId = userStore.currentUser?.userId;

  // >>> "Abrir no contexto" (NoteGeneralForm): filtra pelo DONO da nota clicada,
  // com prioridade sobre o fallback do "usuário que mais postou". Uso único.
  const pendingOwnerId = noteStore.pendingContextOwnerUserId;
  noteStore.pendingContextOwnerUserId = null;

  if (pendingOwnerId !== null && pendingOwnerId !== undefined) {
    if (pendingOwnerId === currentUserId) {
      // Dono é o próprio usuário → "Minhas notas" (padrão já aplicado acima).
      noteStore.filterIncludeOwn = true;
      noteStore.filterIncludeSelectedUsers = false;
      noteStore.selectedUserIdsForFilter = [];
    } else {
      noteStore.filterIncludeOwn = false;
      noteStore.filterIncludeSelectedUsers = true;
      noteStore.selectedUserIdsForFilter = [pendingOwnerId];
    }
  } else {
    const hasOwnNotes = noteStore.allNotes.some(
      (note) => note.createdByUserId === currentUserId
    );

    if (!hasOwnNotes && noteStore.allNotes.length > 0) {
      const topUserId = noteStore.mostFrequentNoteCreatorId;
      if (topUserId !== null && topUserId !== currentUserId) {
        noteStore.filterIncludeOwn = false;
        noteStore.filterIncludeSelectedUsers = true;
        noteStore.selectedUserIdsForFilter = [topUserId];
      }
    }
  }

  await noteStore.setNoteFilters({
    includeOwn: noteStore.filterIncludeOwn,
    comments: noteStore.filterIncludeComments,
    sortBy: noteStore.filterSortOrder,
    includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
  });

  // Espera qualquer disparo residual do watcher (das mutações acima) ser
  // processado e ignorado antes de "abrir a porteira" — evita um refetch
  // redundante logo depois deste.
  await nextTick();
  hasCompletedInitialLoad.value = true;
}

onMounted(async () => {
  const activityId = activityStore.idActivity;

  if (activityId) {
    await loadInitialNotes(activityId);
    window.dispatchEvent(new CustomEvent("note-list-reloaded"));
  }

  // >>> NOVO: começa a escutar o toggle de modo IA do TextDisplayer
  window.addEventListener("ai-note-mode-changed", handleAiNoteModeChanged);

  // Toast de sucesso de EditNoteForm aberto pela lista (NoteCrudDropdown)
  window.addEventListener("note-action-toast", handleNoteActionToast);

  // Atalho "Adicionar notas em lote" do rodapé do mini-modal de trechos
  // (ExcerptMultiSelectMiniModal, sob TextDisplayer): mesmo destino do item
  // "Criar notas em lote" do menu de ações.
  window.addEventListener("request-bulk-add-notes", openBulkAddModal);
});

/* =========================================================
     ✅ RESETAR SELEÇÕES AO SAIR DA ROTA
  ========================================================= */
onBeforeUnmount(() => {
  // Zera os checkboxes selecionados
  noteStore.selectedNoteIds = [];

  // Se fizer sentido pra você, também pode limpar os trechos múltiplos:
  if ("multiSelectedExcerpts" in noteStore) {
    noteStore.multiSelectedExcerpts = [];
  }

  // ✅ PATCH: limpa timeout do toast local
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }

  // >>> NOVO: remove listener ao desmontar
  window.removeEventListener("ai-note-mode-changed", handleAiNoteModeChanged);
  window.removeEventListener("note-action-toast", handleNoteActionToast);
  window.removeEventListener("request-bulk-add-notes", openBulkAddModal);
});
</script>

<template>
  <h2 class="notes-title">Notas</h2>

  <div class="notes-header notes-header--stacked">
    <div class="notes-search-row">
      <SearchNoteInput />

      <p class="notes-search-hint">
        Use <strong>#</strong> para buscar <strong>somente</strong> por tag:
        #dúvida, #resumo
      </p>
    </div>

    <div class="notes-actions-row">
      <div class="note-actions-compact">
        <DetachedNoteButton
          :activityCreatedByUserId="activityStore.selectedActivity?.createdByUserId"
          @add-detached="openDetachedModal"
        />
        <BulkActionNoteButton
          :hasSelectedExcerpts="hasSelectedExcerpts"
          :hasSelectedNotes="noteStore.selectedNoteIds.length > 0"
          :activityCreatedByUserId="activityStore.selectedActivity?.createdByUserId"
          @add-detached="openDetachedModal"
          @bulk-add="openBulkAddModal"
          @bulk-edit="openBulkEditModal"
          @bulk-remove="openBatchDeleteModal"
        />
        <NoteFilter />
      </div>
    </div>
  </div>

  <div class="note-pagination-row">
    <!-- Sob o foco por trecho não há páginas a percorrer: a lista é o recorte
         inteiro. Os controles voltam com o "Ver todas" do NoteList. -->
    <div v-if="!noteStore.isNoteFocusActive" class="note-pagination-left">
      <PaginationButtons
        :current-page="noteStore.currentPage"
        :page-size="noteStore.pageSize"
        :total-count="noteStore.totalCount"
        :total-pages="noteStore.totalPages"
        @change-page="
          (page) => {
            noteStore.fetchNotesByActivityIdPaged(
              activityStore.idActivity,
              page,
              noteStore.pageSize,
              noteStore.searchTerm
            );
          }
        "
      />

      <!-- ✅ NOVO: info de paginação separado -->
      <PaginationInfo
        :current-page="noteStore.currentPage"
        :page-size="noteStore.pageSize"
        :total-count="noteStore.totalCount"
      />
    </div>

    <div class="note-pagination-right">
      <span
        v-if="noteStore.selectedNoteIds.length > 0"
        class="selection-badge"
      >
        {{ noteStore.selectedNoteIds.length }}
        {{
          noteStore.selectedNoteIds.length === 1
            ? "nota"
            : "notas"
        }}
      </span>

      <label v-if="anySelectableOnPage" class="note-checkbox-all-label">
        <input
          id="note-checkbox-all"
          type="checkbox"
          v-model="isAllSelectedOnPage"
          class="note-checkbox-all"
        />
      </label>
    </div>
  </div>

  <NoteList
    :userId="userStore.currentUser?.userId"
    :textId="textStore.idText"
    :textCreatedByUserId="textStore.selectedText?.createdByUserId"
    :textUpdatedByUserId="textStore.selectedText?.updatedByUserId"
    :activityCreatedByUserId="activityStore.selectedActivity?.createdByUserId"
    :isLoadingNotes="noteStore.isLoadingNotes"
    @notify="handleNotify"
    @add-detached="openDetachedModal"
  />

  <BulkAddNoteForm
    :show="showBulkAddModal"
    :excerpts="bulkExcerpts"
    :activityId="activityStore.idActivity"
    :createdByUserId="activityStore.selectedActivity?.createdByUserId"
    @close="handleBulkAddClose"
    @created="handleBulkNotesCreated"
  />

  <!-- ✅ EXISTENTE: Modal de nota avulsa padrão (offsets 0/0) -->
  <AddDetachedNoteForm
    :show="showDetachedNoteModal"
    :activityId="activityStore.idActivity"
    :createdByUserId="activityStore.selectedActivity?.createdByUserId"
    @close="closeDetachedModal"
  />

  <!-- >>> NOVO: Modal de nota avulsa com IA (offsets 0/0) -->
  <AddAiDetachedNoteForm
    :show="showAiDetachedNoteModal"
    :activityId="activityStore.idActivity"
    :createdByUserId="activityStore.selectedActivity?.createdByUserId"
    @close="closeAiDetachedModal"
  />

  <!-- ✅ NOVO: Modal de edição em lote -->
  <BulkEditNoteForm :show="showBulkEditModal" @close="closeBulkEditModal" />

  <!-- ✅ NOVO: Modal de deleção em lote -->
  <BatchDeleteNoteForm
    v-if="showBatchDeleteModal"
    @close="closeBatchDeleteModal"
  />

  <!-- ✅ PATCH: Toast local do NoteSection.vue -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error'
              ? 'toast-error'
              : toastType === 'info'
              ? 'toast-info'
              : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />

              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />

              <path
                v-else-if="toastType === 'info'"
                d="M10 9v5M10 6.5v1"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />

              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{
                toastType === "error"
                  ? "Erro ao excluir"
                  : toastType === "info"
                  ? "Como fazer"
                  : "Sucesso!"
              }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            X
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style>
/* (seu CSS original mantido, com pequena inclusão para o novo layout) */

/* Título "Notas". Por classe, não por `h2`: este <style> é global e o CSS
   dele carrega antes do main.css (App -> stores -> router -> RightArea), então
   o reset do Tailwind (`h2 { font-weight: inherit; font-size: inherit }`)
   vencia um seletor de elemento e o título saía sem negrito. Classe vence o
   reset em qualquer ordem, como o .reader-header-label de "Atividade". */
.notes-title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 6px 0;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.notes-search {
  flex-grow: 1;
  display: flex;
  align-items: center;
  background-color: var(--surface);
  border-radius: 12px;
  padding: 0 12px;
}

.notes-search i {
  color: var(--text-light);
}

.notes-search input {
  border: none;
  outline: none;
  padding: 10px 8px;
  width: 100%;
  font-size: 0.9rem;
  background: transparent;
}

.filters {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.filter-button {
  width: 120px;
  border-radius: 12px;
  justify-content: space-between;
}

.actions-button {
  width: 120px;
  height: 40px;
  border-radius: 12px;
  justify-content: space-between;
}

.tag {
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: filter 0.2s ease-in-out;
}

.tag:hover {
  filter: brightness(0.95);
}

.tag-draft {
  background-color: #d6bcfa;
  color: #5f3dc4;
}

.tag-personal {
  background-color: #b2f2bb;
  color: #2b8a3e;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
  padding-right: 12px;
}

.note-card {
  background-color: var(--surface);
  border-radius: 16px;
  padding: 16px;
}

.note-card-header {
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.author-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.author-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--border-color);
  background-size: cover;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  color: var(--text-light);
}

.note-actions i {
  cursor: pointer;
  transition: color 0.2s;
}

.note-actions i:hover {
  color: var(--text-dark);
}

.note-card .note-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.note-card .note-content {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-light);
  margin: 0 0 16px 0;
}

.note-tags-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--text-light);
  margin-bottom: 16px;
}

.note-tags-line .fa-tag {
  color: var(--text-dark);
}

.subcategory-tag {
  background-color: var(--bg-active-nav);
  color: var(--text-dark);
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 500;
}

.note-timestamp,
.note-source {
  text-align: right;
  font-size: 0.8rem;
  color: var(--text-light);
  margin: 4px 0 0 0;
}

.note-source {
  font-style: italic;
}

/* OVERRIDES */
.notes-header {
  gap: 12px;
}

.filters {
  gap: 6px;
}

.notes-search input {
  padding: 8px 6px;
  font-size: 0.85rem;
}

.notes-list {
  gap: 14px;
  padding-right: 10px;
}

.note-card {
  padding: 14px;
}

.note-card .note-title {
  font-size: 1rem;
  margin: 0 0 6px 0;
}

.note-card .note-content {
  font-size: 0.85rem;
  line-height: 1.55;
  margin: 0 0 12px 0;
}

.note-tags-line {
  font-size: 0.8rem;
  margin-bottom: 12px;
}

.note-timestamp,
.note-source {
  font-size: 0.75rem;
}

/* Layout empilhado do header */
.notes-header--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

/* Linha da busca */
.notes-search-row {
  width: 100%;
}

/* dica abaixo da busca */
.notes-search-hint {
  margin: 4px 0 0 0;
  font-size: 0.58rem;
  line-height: 1.2;
  color: var(--text-light);
  opacity: 0.95;
  user-select: none;
  margin-left: 10px;
  margin-bottom: 5px;
}

/* Linha dos botões */
.notes-actions-row {
  display: flex;
  justify-content: flex-end;
}

/* botões no padrão do TextDisplayer (compact-controls) */
.note-actions-compact {
  display: flex;
  align-items: center;
  gap: 6px;
}

.note-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  margin-bottom: 8px;
  font-size: 0.82rem;
}

/* ✅ Agrupa botões + info à esquerda */
.note-pagination-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Agrupa o contador de seleção + checkbox-all à direita */
.note-pagination-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selection-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

.note-checkbox-all-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 20px; /* ✅ pequena margem à esquerda do checkbox-all */
}

.note-checkbox-all {
  width: 0.9rem;
  height: 0.9rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  margin: 0;
  transition: all 0.15s ease-in-out;
}

.note-checkbox-all:hover {
  border-color: var(--brand-light);
}

.note-checkbox-all:checked {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
}

.note-checkbox-all:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 2px;
  width: 4px;
  height: 7px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.note-checkbox-all:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.note-checkbox-all-text {
  color: var(--text-light);
}

/* >>> NOVO: variante informativa (instruções "Como fazer") */
.toast-info {
  background-color: #e0f2fe;
  border-left: 6px solid #0ea5e9;
  color: #0c4a6e;
}

.toast-info .toast-icon-circle {
  fill: #e0f2fe;
  stroke: #0ea5e9;
}
</style>
